<template>
  <div>
    <Loading v-if="!loadStatus" text="Loading gifts..." />
    <div v-if="loadStatus && selectableTiers.length">
      <GroupOrderHeaderBand
        :showFixedHeader="false"
        :special-instruction="subtitle"
      />

      <div class="group-order-container">
        <component
          v-for="(tier, index) in selectableTiers"
          :key="tier.id"
          :tier="tier"
          :sequence="index + 1"
          :selectedVariants="
            selectedVariants.filter(
              variant => parseInt(variant.tierId) === parseInt(tier.id)
            )
          "
          :activeItems="
            activeItemsPerTier.filter(
              item => parseInt(item.tierId) === parseInt(tier.id)
            )
          "
          :is="'GroupOrderTierSelection'"
          :payload="payload"
          @handleSelection="product => payload.push(product)"
          @removeSelection="item => payload.splice(payload.indexOf(item), 1)"
          @handleSingleProduct="handleSingleProduct"
          @updateProductVariant="updateProductVariant"
        ></component>
      </div>
    </div>
    <section class="region-footer">
      <div class="button-region">
        <p></p>
        <div class="button-holder">
          <p v-if="showValidationError">
            Please select the required no. of items to proceed.
          </p>
          <p v-if="showOptionsError">
            {{ errorMessage }}
          </p>

          <button
            :class="{ 'button-gemnote--disabled': !disabledConfirm }"
            :disabled="!disabledConfirm"
            class="button-gemnote button-gemnote--small"
            @click="submitSelection"
          >
            <span>next</span>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { progressStatus, layouts } from '../../helpers'
import GroupOrderHeaderBand from './GroupOrderHeaderBand'
import GroupOrderTierSelection from './GroupOrderTierSelection'

export default {
  name: 'GroupOrderTierList',
  components: {
    GroupOrderHeaderBand,
    GroupOrderTierSelection
  },

  data: () => ({
    payload: [],
    showValidationError: false,
    showOptionsError: false,
    validationTriggered: false,
    pageLoaded: false
  }),

  computed: {
    subtitle() {
      return this.groupOrderDetails.redemptionSubheading
    },
    loadStatus() {
      return progressStatus(this.loadGroupOrderTiersForRecipient)
    },
    disabledConfirm() {
      if (!this.validationTriggered) {
        return true // Initially true until validation is triggered
      }
      let canCheckout = false

      const activeSelectableTiers = this.selectableTiers.filter(
        tier => tier.tierDetails.length
      )
      // Check if all tiers are of type 'UPTO'
      const allUptoTiers = activeSelectableTiers.every(
        tier => tier.selectionType === 'UPTO'
      )

      if (allUptoTiers) {
        // If all tiers are 'UPTO', require at least one selection
        canCheckout = this.payload.length > 0
      } else {
        // If not all tiers are 'UPTO', skip the 'UPTO' tiers check
        canCheckout = true

        activeSelectableTiers.forEach(tier => {
          let selectedTierProducts = this.payload.filter(
            item => item.tierId === tier.id
          )

          // Skip check for 'UPTO' tiers
          if (tier.selectionType === 'UPTO') {
            return
          }

          canCheckout = this.payload.every(product => {
            if (product.type === 'product') {
              if (tier.selectionType && tier.selectionType === 'EXACTLY') {
                return (
                  Boolean(product.variantId) &&
                  selectedTierProducts.length === tier.giftsLimit
                )
              }
              return Boolean(product.variantId)
            }
            if (product.type === 'bundle') {
              return product.items.every(bundleItem => {
                if (!bundleItem.variantId) {
                  return false
                }

                if (tier.selectionType && tier.selectionType === 'EXACTLY') {
                  return (
                    Boolean(product.variantId) &&
                    selectedTierProducts.length === tier.giftsLimit
                  )
                }
                return true
              })
            }
            return Boolean(product.variantId)
          })

          if (tier.selectionType && tier.selectionType === 'EXACTLY') {
            canCheckout = selectedTierProducts.length === tier.giftsLimit
          }
        })
      }

      return canCheckout
    },

    ...mapState({
      loadGroupOrderTiersForRecipient: state =>
        state.groupOrder.loadGroupOrderTiersForRecipient,
      selectableTiers: state => state.groupOrder.selectableTiers,
      selectedVariants: state => state.groupOrder.selectedVariants,
      activeItemsPerTier: state => state.groupOrder.activeItemsPerTier,
      orderRecipient: state => state.groupOrder.recipient,
      uuid: state => state.groupOrder.groupOrderDetails.uuid,
      groupOrderDetails: state => state.groupOrder.groupOrderDetails
    })
  },

  watch: {
    payload: {
      handler() {
        if (this.pageLoaded) {
          this.checkIfValid()
        }
      },
      deep: true
    }
  },

  async created() {
    this.$store.dispatch('layout/setPageLayout', layouts.GROUP_ORDER_LAYOUT)
    this.$store.dispatch('groupOrder/getGroupOrderPublicInfo', this.uuid)
    this.$store.dispatch('groupOrder/getRecipientGroupOrderTiers')
    this.pageLoaded = true
  },

  mounted() {
    this.retrieveOrderPayload()
  },

  methods: {
    checkIfValid() {
      this.showError(false, ``)

      const tiers = this.selectableTiers.filter(tier => tier.tierDetails.length)
      let errorMessage = ''
      let tierErrorCount = 0

      // Check if all tiers are of type 'UPTO'
      const allUptoTiers = tiers.every(tier => tier.selectionType === 'UPTO')

      if (allUptoTiers) {
        // If all tiers are 'UPTO', require at least one selection
        if (this.payload.length === 0) {
          this.showError(true, `At least one selection is required.`)
        } else {
          this.showError(false, ``)
        }
      } else {
        // If not all tiers are 'UPTO', check for other tier types
        if (tiers.length) {
          tiers.forEach((tier, index) => {
            let selectedProducts = this.payload.filter(
              item => item.tierId === tier.id
            )

            if (tier.selectionType === 'EXACTLY') {
              if (selectedProducts.length !== tier.giftsLimit) {
                errorMessage += `Tier ${index + 1}` + ','
                tierErrorCount += 1
              }
            } else if (
              tier.selectionType === 'UPTO' &&
              selectedProducts.length === 0
            ) {
              // Skip checking 'UPTO' tiers if no items are selected
              return
            }
          })

          if (errorMessage) {
            if (tierErrorCount === 1) {
              const message = errorMessage.split(',')
              this.showError(
                true,
                `${message[0]} ${tierErrorCount > 1 ? 'are' : 'is'} incomplete`
              )
            } else {
              this.showError(
                true,
                `${errorMessage} ${
                  tierErrorCount > 1 ? 'are' : 'is'
                } incomplete`
              )
            }
          } else {
            this.showError(false, ``)
          }
        } else {
          this.showError(false, ``)
        }
      }
    },

    showError(show, message) {
      this.showOptionsError = show
      this.errorMessage = message
    },

    handleSingleProduct(product) {
      this.payload = this.payload.filter(p => p.tierId != product.tierId)
      this.payload.push(product)
    },

    updateProductVariant(data) {
      const productIndex = this.payload.findIndex(
        item => item.itemId === data.itemId
      )

      if (productIndex > -1) {
        const product = this.payload[productIndex]

        if (product.type === 'bundle') {
          let bundleItemIndex = product.items.findIndex(
            item => item.itemId === data.bundleItemId
          )

          const bundleItem = product.items[bundleItemIndex]
          bundleItem.variantId = data.value
          product.items[bundleItemIndex] = bundleItem

          this.$set(this.payload, productIndex, product)
        }

        if (product.type === 'product') {
          product.variantId = data.value
          this.$set(this.payload, productIndex, product)
        }
      }
    },

    submitSelection() {
      this.validationTriggered = true
      this.checkIfValid()
      if (!this.showOptionsError) {
        this.$store.dispatch('groupOrder/handleReceipientSelection', {
          payload: this.payload,
          recipientId: this.orderRecipient ? this.orderRecipient.id : ''
        })
      }
    },

    retrieveOrderPayload() {
      const { id } = this.orderRecipient
      const order = localStorage.getItem(`groupOrderPayload${id}`)

      if (order && order.length) {
        this.payload = JSON.parse(order)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.button-region {
  width: 60%;
  justify-content: space-between;
  display: flex;
  align-items: center;
  gap: 2rem;

  .button-holder {
    display: flex;
    align-items: center;

    p {
      color: #ce3c3c;
      font-size: 0.625rem;
      margin-right: 0.5rem;
      font-weight: 700;
    }
  }

  @media (max-width: 575.98px) {
    justify-content: center;
    width: 100%;
  }

  @media (min-width: 576px) and (max-width: 767.98px) {
    justify-content: center;
    width: 100%;
  }

  p {
    font-size: 0.875rem;
    color: #232220;

    span {
      color: #a09c95;
    }
  }

  .button-gemnote {
    height: 2.5rem;
    width: auto;
    padding: 0 2rem !important;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.campaign-subtitle {
  width: 60%;
  font-size: 1rem;
  text-align: center;
  margin: 1.75rem auto;
}

.button-gemnote {
  height: 2.5rem;
  width: auto;
  padding: 0 2rem !important;
}

.group-order-container {
  position: relative;
  top: 1rem;
  margin-bottom: 8rem;
}
</style>
